import { defineComponent } from 'vue';
import { ElDialog, ElIcon } from 'element-plus';
import { Close } from '@element-plus/icons-vue';
export default defineComponent({
    name: 'GxDialog',
    components: {
        ElDialog,
        ElIcon,
        Close
    },
    inheritAttrs: false,
    props: {
        /**
         * 对话框的显示状态，支持 v-model
         */
        modelValue: {
            type: Boolean,
            default: false
        },
        /**
         * 对话框标题
         */
        title: {
            type: String,
            default: ''
        },
        /**
         * 是否显示关闭按钮
         */
        showClose: {
            type: Boolean,
            default: true
        },
        /**
         * 对话框尺寸(宽度)
         * @values 'large' | 'default' | 'small' | 'fullscreen'
         */
        size: {
            type: String,
            default: 'default',
            validator: (value) => ['large', 'default', 'small', 'fullscreen'].includes(value)
        },
        /**
         * 对话框模式
         * @values 'default' | 'description'
         */
        mode: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'description'].includes(value)
        }
    },
    emits: ['update:modelValue', 'close'],
    computed: {
        /**
         * 传递给 el-dialog 的属性
         * 始终排除 width 属性，由 size prop 来控制宽度（默认值为 'default'）
         * 当 size 为 'fullscreen' 时，传递 fullscreen: true
         */
        dialogAttrs() {
            const { width, ...restAttrs } = this.$attrs;
            const attrs = {
                ...restAttrs
            };
            if (this.size === 'fullscreen') {
                attrs.fullscreen = true;
            }
            return attrs;
        },
        /**
         * 对话框类名，用于应用 size 和 mode 样式
         * size 和 mode 可以同时应用，互不冲突
         */
        dialogClassNames() {
            const classNames = [
                'gx-dialog',
                `gx-dialog--size-${this.size}`,
                `gx-dialog--mode-${this.mode}`
            ];
            return classNames;
        },
        /**
         * 检查 footer slot 是否有内容
         * 会过滤掉空白字符、空内容等情况
         */
        hasFooterContent() {
            const footerSlotContent = this.$slots.footer?.();
            if (!footerSlotContent || footerSlotContent.length === 0) {
                return false;
            }
            /**
             * 递归检查 VNode 是否有实际可见内容
             * @param vnode - Vue VNode 节点
             * @returns 是否有实际内容
             */
            const hasVisibleContent = (vnode) => {
                if (!vnode) {
                    return false;
                }
                // 处理 VNode 数组
                if (Array.isArray(vnode)) {
                    return vnode.some(node => hasVisibleContent(node));
                }
                // 处理字符串（文本节点）
                if (typeof vnode === 'string') {
                    return vnode.trim().length > 0;
                }
                // 处理数字
                if (typeof vnode === 'number') {
                    return true;
                }
                // 处理 VNode 对象
                if (typeof vnode === 'object' && vnode !== null) {
                    if ('children' in vnode) {
                        return hasVisibleContent(vnode.children);
                    }
                    else {
                        return true;
                    }
                }
                return false;
            };
            return footerSlotContent.some((vnode) => hasVisibleContent(vnode));
        }
    },
    methods: {
        /**
         * 处理 modelValue 更新
         */
        handleUpdateModelValue(value) {
            this.$emit('update:modelValue', value);
        },
        /**
         * 处理关闭按钮点击
         */
        handleCloseButtonClick() {
            this.$emit('update:modelValue', false);
            this.$emit('close');
        },
        /**
         * 处理对话框关闭事件
         */
        handleClose() {
            this.$emit('close');
        }
    }
});
